import {
  InputAdornmentProps,
  InputAdornment as MuiInputAdornment,
  TextField as MuiTextField,
  PopoverProps,
  TextFieldProps,
  useFormControl,
} from '@mui/material';
import cx from 'classnames';
import { isEmpty, omit } from 'lodash';
import React, { forwardRef, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { MomentFormats } from '../../../../../common/utils/date';
import { withThemeNext } from '../../../../layout/theme-next';
import { useInputStyles, useStyles } from './Datepicker.styles';
import { DatePickerProps } from './Datepicker.types';

const PROPS_TO_SKIP: Array<keyof TextFieldProps> = [
  'onKeyUp',
  'variant',
  'contentEditable',
  'draggable',
  'spellCheck',
  'margin',
  'helperText',
];

type CustomTextFieldProps = {
  onClear?: () => void
} & Partial<TextFieldProps>;

const SafeInputAdornment = forwardRef<HTMLDivElement, InputAdornmentProps>((props, ref) => (
  <MuiInputAdornment ref={ref} {...props} />
));
SafeInputAdornment.displayName = 'SafeInputAdornment';

const DatepickerTextField: React.FC<CustomTextFieldProps> = ({
  onKeyDown,
  onClear,
  InputProps,
  ...props
}) => {
  const classes = useInputStyles();
  const suitableProps = omit(props, ...PROPS_TO_SKIP);

  const onDateClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  const isClearVisible = onClear && !isEmpty(props.value);

  return (
    <MuiTextField
      onKeyDown={onKeyDown}
      {...suitableProps}
      InputProps={{
        ...InputProps,
        endAdornment: (
          <SafeInputAdornment
            position="end"
            className={cx({
              [classes.endAdorment]: !props.disabled,
              [classes.endAdormentDisabled]: props.disabled,
            })}
          >
            {isClearVisible && (
              <FontAwesomeIcon
                className={classes.clear}
                data-cy="date-clear"
                icon={regular('xmark')}
                onClick={onDateClear}
              />
            )}
            <FontAwesomeIcon data-cy="calendar-day" icon={regular('calendar-day')} />
          </SafeInputAdornment>
        ),
      }}
    />
  );
};

const DEFAULT_POPOVER_PROPS: Partial<PopoverProps> = {
  elevation: 2,
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
};

const Datepicker: React.FC<DatePickerProps> = ({
  // Removed deprecated props from MUI v5 DatePicker:
  // autoOk = true,
  // disableToolbar = true,
  // initialFocusedDate = null,
  format = MomentFormats.MonthDateYearTwoDigit,
  // variant = 'inline', // no longer supported
  PopoverProps,
  onBlur,
  onClear,
  ...props
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const muiFormControl = useFormControl();

  const controlState = {
    disabled: props.disabled ?? muiFormControl?.disabled ?? false,
    // error: props.error ?? muiFormControl?.error ?? false,
    // required: props.required ?? muiFormControl?.required ?? false,
  };

  const [blurEvent, setBlurEvent] = useState<React.FocusEvent<HTMLDivElement> | null>(null);
  const [isClosed, setIsClosed] = useState(false);

  const finalPopoverProps = {
    ...DEFAULT_POPOVER_PROPS,
    ...PopoverProps,
    classes: {
      paper: classes.popover,
      ...(PopoverProps?.classes || {}),
    },
  };

  const handleClose = () => {
    if (blurEvent && onBlur) {
      setIsClosed(true);
    }
  };

  useEffect(() => {
    if (blurEvent && isClosed && onBlur) {
      onBlur(blurEvent);
      setBlurEvent(null);
      setIsClosed(false);
    }
  }, [blurEvent, isClosed, onBlur]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={intl.locale}>
      <DatePicker
        {...props}
        disabled={controlState.disabled}
        // error={controlState.error}
        onClose={handleClose}
        // onBlur={(event) => {
        //   if (onBlur) {
        //     setBlurEvent(event as React.FocusEvent<HTMLDivElement>);
        //   }
        // }}
        inputFormat={format}
        PopperProps={finalPopoverProps}
        renderInput={(params) => <DatepickerTextField {...params} onClear={onClear} />}
        // You can optionally provide custom icons or other components here if needed
      />
    </LocalizationProvider>
  );
};

export default withThemeNext(Datepicker);
